import React, { createContext, useState, useEffect, useRef } from "react";
import { v4 as uuidv4 } from "uuid";
import { getFileToken } from "../utils/uploadThumbnail";

export const UploadContext = createContext();

export const UploadProvider = ({ children }) => {
  const [uploads, setUploads] = useState({});
  const [uploadCompleted, setUploadCompleted] = useState(false);
  const cancelledUploadsRef = useRef(new Set());

  // Upload file in chunks
  const uploadFileInChunks = async (file, tokenData, onProgress) => {
    let chunkSize = tokenData.next_chunk_size;
    const totalFileSize = file.size;
    let uploadedBytes = 0;
    let chunkNum = 1;

    while (uploadedBytes < totalFileSize) {
      const chunk = file.slice(
        uploadedBytes,
        Math.min(uploadedBytes + chunkSize, totalFileSize)
      );
      const formData = new FormData();
      formData.append("chunk", new Blob([chunk]));
      formData.append("chunk_no", chunkNum);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_API_VIDEO_PATH}/file-upload/upload`,
          {
            method: "POST",
            headers: {
              X_FILE_TOKEN: tokenData.token,
            },
            body: formData,
          }
        );
        if (!response.ok) throw new Error(`Failed to upload chunk ${chunkNum}`);
        const resData = await response.json();
        uploadedBytes = Math.min(uploadedBytes + chunkSize, totalFileSize);
        if (onProgress)
          onProgress(Math.round((uploadedBytes / totalFileSize) * 100));
        chunkSize = resData.next_chunk_size;
      } catch (error) {
        throw error;
      }
      chunkNum += 1;
    }

    // Complete the upload
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_VIDEO_PATH}/file-upload/complete`,
        {
          method: "POST",
          headers: {
            X_FILE_TOKEN: tokenData.token,
          },
          body: JSON.stringify({}),
        }
      );
      if (!response.ok) throw new Error("Failed to complete upload");
      const data = await response.json();
      return data.aws_file_key;
    } catch (error) {
      throw error;
    }
  };

  // Add attachment
  const addAttachment = async (task, awsFileKey) => {
    try {
      const body = {
        entity: 0,
        entity_id: task.taskId,
        aws_file_ids: [awsFileKey],
        categories: task.categories,
        category: "facebook",
        subcategory: task.fileType,
      };

      const response = await fetch(
        `${process.env.REACT_APP_API_PATH}/uploads/`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${task.userToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        }
      );
      if (!response.ok) throw new Error("Failed to add attachment");
      const data = await response.json();
      return { success: true, data };
    } catch (error) {
      return { success: false, error: error.message };
    }
  };

  const uploadFiles = async (files, options) => {
    if (!files.length) return;
    const fileIds = [];
    const initialUploads = {};

    setUploadCompleted(false);
    // Prepare all uploads with 0% progress
    for (const file of files) {
      const fileId = uuidv4();
      fileIds.push(fileId);
      initialUploads[fileId] = {
        file,
        status: "uploading",
        progress: 0,
      };
    }
    setUploads((prev) => ({
      ...prev,
      ...initialUploads,
    }));

    for (let i = 0; i < files.length; i++) {
      const fileId = fileIds[i];
      const file = files[i];

      if (cancelledUploadsRef.current.has(fileId)) continue;
      // Start upload process
      try {
        // 1. Get file token
        const response = await getFileToken(file.name);
        if (response.error) throw new Error("Could not get file token");

        // 2. Upload file in chunks
        const awsFileKey = await uploadFileInChunks(
          file,
          response.data,
          (progress) => {
            setUploads((prev) => ({
              ...prev,
              [fileId]: {
                ...prev[fileId],
                progress,
                status: "uploading",
              },
            }));
          }
        );

        // 3. Add attachment
        const attachResult = await addAttachment(
          {
            ...options,
            userToken: localStorage.getItem("token"),
          },
          awsFileKey
        );

        if (!attachResult.success) throw new Error(attachResult.error);

        // 4. Mark as complete
        setUploads((prev) => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            status: "completed",
            progress: 100,
          },
        }));

        // 5. Remove after 5 minutes
        setTimeout(() => {
          setUploads((prev) => {
            const newUploads = { ...prev };
            delete newUploads[fileId];
            return newUploads;
          });
        }, 5 * 60 * 1000);
      } catch (error) {
        setUploads((prev) => ({
          ...prev,
          [fileId]: {
            ...prev[fileId],
            status: "error",
            error: error.message,
          },
        }));
      }
    }

    setUploadCompleted(true);
    return fileIds;
  };

  // Cancel an upload
  const cancelUpload = (fileId) => {
    cancelledUploadsRef.current.add(fileId);
    setUploads((prev) => {
      const newUploads = { ...prev };
      delete newUploads[fileId];
      return newUploads;
    });
  };

  return (
    <UploadContext.Provider
      value={{
        uploads,
        uploadFiles,
        cancelUpload,
        uploadCompleted,
      }}
    >
      {children}
    </UploadContext.Provider>
  );
};
